import { API_PATHS, interpolatePathParams, ROUTE_PARAMS } from '@futrli/shared-services';
import { FinancialConfig, AvailableLineData, ChartOfAccounts, AvailableLineDataRowFormulas } from '@futrli-sdk/predict';

import { makeRequestWithResponse } from './shared';

/**
 * Gets a financial config for an organisation
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @throws error
 * @returns Promise<T>
 */
export const fetchFinancialConfig = async (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): Promise<FinancialConfig> => {
  const path = interpolatePathParams(
    isConsolidation ? API_PATHS.CURRENT_FINANCIAL_CONFIG_CONSOLIDATION : API_PATHS.CURRENT_FINANCIAL_CONFIG,
    {
      [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
      [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
    }
  );

  return makeRequestWithResponse<FinancialConfig>({ url: path, method: 'GET' });
};

/**
 * Gets a financial config
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - Forecast id
 * @throws error
 * @returns Promise<T>
 */
export const fetchLineData = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string
): Promise<AvailableLineData> => {
  const path = interpolatePathParams(API_PATHS.AVAILABLE_LINE_DATA_FORMULAS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<AvailableLineData>({ url: path, method: 'GET' });
};

/**
 * Gets chart of accounts data
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get forecasts from
 * @param {string} forecastId - Forecast id
 * @throws error
 * @returns Promise<T>
 */
export const fetchChartOfAccounts = async (
  workspaceId: string,
  organisationId: string,
  forecastId: string
): Promise<ChartOfAccounts> => {
  const path = interpolatePathParams(API_PATHS.CHART_OF_ACCOUNTS, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.FORECAST_ID]: forecastId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<ChartOfAccounts>({ url: path, method: 'GET' });
};

/**
 * Fetch Available line data for row formula
 *
 * @param {string} workspaceId - workspace to get the organisations from
 * @param {string} organisationId - organisation to get plays from
 * @returns {Promise<>} - Returns Available line data for row formula
 * @throws error
 */
export const fetchAvailableRowFormulaLineData = async (
  workspaceId: string,
  organisationId: string,
  isConsolidation?: boolean
): Promise<AvailableLineDataRowFormulas> => {
  const route = isConsolidation
    ? API_PATHS.AVAILABLE_LINE_DATA_ROW_FORMULA_CONSOLIDATION
    : API_PATHS.AVAILABLE_LINE_DATA_ROW_FORMULA_V2;

  const path = interpolatePathParams(route, {
    [ROUTE_PARAMS.ORGANISATION_ID]: organisationId,
    [ROUTE_PARAMS.WORKSPACE_ID]: workspaceId
  });

  return makeRequestWithResponse<AvailableLineDataRowFormulas>({ url: path });
};
